import React from 'react';
import { Box, Typography, Dialog, DialogContent, IconButton, Chip, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { priorityStyles, categoryStyles } from './noticePriorityStyles';
import type { Notice } from '../../../types/notice.types';

interface NoticeDetailDialogProps {
  open: boolean;
  onClose: () => void;
  notice: Notice | null;
}

/**
 * 공지사항 상세 다이얼로그
 * 티커 클릭 시 또는 필요 시 사용
 */
const NoticeDetailDialog: React.FC<NoticeDetailDialogProps> = ({ open, onClose, notice }) => {
  if (!notice) return null;

  const pStyle = priorityStyles[notice.priority];
  const cStyle = categoryStyles[notice.category];

  const createdDate = (() => {
    try {
      const d = (notice.created_at as any)?.toDate
        ? (notice.created_at as any).toDate()
        : new Date(notice.created_at as any);
      return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return '';
    }
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          background: pStyle.gradient,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: `2px solid ${pStyle.borderColor}`,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', gap: 0.8, mb: 1 }}>
            <Chip
              label={pStyle.label}
              size="small"
              sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: pStyle.color, color: '#fff' }}
            />
            <Chip
              label={`${cStyle.icon} ${notice.category}`}
              size="small"
              variant="outlined"
              sx={{ height: 20, fontSize: 10, borderColor: `${pStyle.borderColor}40` }}
            />
          </Box>
          <Typography fontWeight={800} sx={{ fontSize: 18, color: '#0f172a' }}>
            {notice.title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
            {createdDate}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, mr: -1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* HTML 본문 */}
        <Box
          sx={{
            fontSize: 15,
            color: '#334155',
            lineHeight: 1.8,
            '& p': { m: 0, mb: 1 },
            '& strong': { fontWeight: 700 },
            '& a': { color: '#2563eb', textDecoration: 'underline' },
            '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
            '& ul, & ol': { pl: 2.5 },
          }}
          dangerouslySetInnerHTML={{ __html: notice.body }}
        />

        {/* 이미지 */}
        {notice.imageUrl && (
          <Box
            component="img"
            src={notice.imageUrl}
            alt={notice.title}
            sx={{ width: '100%', borderRadius: 2, mt: 2, maxHeight: 400, objectFit: 'cover' }}
          />
        )}

        {/* CTA 버튼 */}
        {notice.linkUrl && (
          <Button
            variant="contained"
            href={notice.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            fullWidth
            sx={{
              mt: 3,
              py: 1.3,
              borderRadius: 2.5,
              fontWeight: 700,
              fontSize: 14,
              textTransform: 'none',
              bgcolor: pStyle.color,
              '&:hover': { bgcolor: pStyle.color, opacity: 0.9 },
            }}
          >
            {notice.linkLabel || '자세히 보기'}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NoticeDetailDialog;
